import { useEffect, useRef, useState } from 'react';

import { useDecisions } from '@libs/common-next';

import { useProduct, useSelectedProductStore } from '@/entities/product';
import { setValue, usePurchaseFormWatch } from '@/entities/purchase-form';
import { consumePendingFavorite, type PendingFavorite } from '@/features/favorite-toggle/lib/pending-favorite';
import { useFulfillmentAnalyticsStore } from '@/shared/analytics/model/fulfillment-analytics.store';

type PendingStep =
    | 'add-favorite'
    | 'set-personalization'
    | 'set-purchase-selections'
    | 'settle-purchase-selections'
    | 'set-fulfilment-method'
    | 'settle-fulfilment-method'
    | 'set-quantity'
    | null;

type UsePendingFavoriteAfterAuthProps = {
    sku: string | undefined;
    userId: string | undefined;
    isFavorite: boolean | undefined;
    isFavoritesLoading: boolean;
    isTogglePending: boolean;
    toggleFavorite: (params: { sku: string; action: 'add' }) => void;
};

export const usePendingFavoriteAfterAuth = ({
    sku,
    userId,
    isFavorite,
    isFavoritesLoading,
    isTogglePending,
    toggleFavorite,
}: UsePendingFavoriteAfterAuthProps) => {
    const isAuth0Enabled = useDecisions('auth0');
    const { webStyleId, setSku } = useSelectedProductStore(state => state);
    const { isLoading: tailoredIsLoading } = useProduct();
    const purchaseOption = usePurchaseFormWatch('purchaseOption');
    const autoshipFrequency = usePurchaseFormWatch('autoshipFrequency');
    const autoshipUnit = usePurchaseFormWatch('autoshipUnit');
    const fulfilmentMethod = usePurchaseFormWatch('fulfilmentMethod');

    const pendingFavoriteRef = useRef<PendingFavorite | null>(null);
    const [pendingStep, setPendingStep] = useState<PendingStep>(null);

    const setFulfilmentAnalyticsSuppressed = useFulfillmentAnalyticsStore.getState().setSuppressed;

    useEffect(() => {
        if (!userId) {
            pendingFavoriteRef.current = null;
            setPendingStep(null);
            return;
        }

        if (!isAuth0Enabled || isFavoritesLoading || isTogglePending || !webStyleId) return;

        pendingFavoriteRef.current ??= consumePendingFavorite(webStyleId);

        const pending = pendingFavoriteRef.current;

        if (!pending) return;

        const targetSku = pending.favoriteSku;

        if (pendingStep === null) {
            setPendingStep('add-favorite');
            return;
        }

        // Switch to targetSku before firing so toggle and analytics target the pending variant.
        if (sku !== targetSku) {
            setSku(targetSku);
            return;
        }

        if (pendingStep !== 'add-favorite') return;

        if (!isFavorite) {
            toggleFavorite({ sku: targetSku, action: 'add' });
        }

        setPendingStep('set-personalization');
    }, [
        isAuth0Enabled,
        isFavorite,
        isFavoritesLoading,
        isTogglePending,
        pendingStep,
        setSku,
        sku,
        toggleFavorite,
        userId,
        webStyleId,
    ]);

    useEffect(() => {
        if (!isAuth0Enabled || !userId) {
            setFulfilmentAnalyticsSuppressed(false);
            return;
        }

        const pending = pendingFavoriteRef.current;

        if (!pending || pendingStep === null || pendingStep === 'add-favorite') return;

        const finishPendingRestore = () => {
            pendingFavoriteRef.current = null;
            setFulfilmentAnalyticsSuppressed(false);
            setPendingStep(null);
        };

        if (!pending.selections) {
            finishPendingRestore();
            return;
        }

        const { selections } = pending;

        if (pendingStep === 'set-personalization') {
            if (selections.personalization) {
                setValue('personalization', selections.personalization);
            }
            setPendingStep('set-purchase-selections');
            return;
        }

        if (tailoredIsLoading) return;

        if (pendingStep === 'set-purchase-selections') {
            setFulfilmentAnalyticsSuppressed(true);
            setValue('purchaseOption', selections.purchaseOption);
            setValue('autoshipFrequency', selections.autoshipFrequency);
            setValue('autoshipUnit', selections.autoshipUnit);
            setPendingStep('settle-purchase-selections');
            return;
        }

        // Let useSelectDeliveryMethod process purchase selections.
        if (pendingStep === 'settle-purchase-selections') {
            if (
                purchaseOption !== selections.purchaseOption ||
                autoshipFrequency !== selections.autoshipFrequency ||
                autoshipUnit !== selections.autoshipUnit
            ) {
                finishPendingRestore();
                return;
            }
            setPendingStep('set-fulfilment-method');
            return;
        }

        if (pendingStep === 'set-fulfilment-method') {
            setValue('fulfilmentMethod', selections.fulfilmentMethod);
            setPendingStep('settle-fulfilment-method');
            return;
        }

        // Let useSelectDeliveryMethod and useQuantity process fulfilmentMethod.
        if (pendingStep === 'settle-fulfilment-method') {
            if (fulfilmentMethod !== selections.fulfilmentMethod) {
                finishPendingRestore();
                return;
            }
            setPendingStep('set-quantity');
            return;
        }

        setValue('quantity', selections.quantity);
        finishPendingRestore();
    }, [
        autoshipFrequency,
        autoshipUnit,
        fulfilmentMethod,
        isAuth0Enabled,
        pendingStep,
        purchaseOption,
        setFulfilmentAnalyticsSuppressed,
        tailoredIsLoading,
        userId,
    ]);

    useEffect(() => {
        return () => setFulfilmentAnalyticsSuppressed(false);
    }, [setFulfilmentAnalyticsSuppressed]);
};
